/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { formatDate } from '@squiz/dxp-console-support';
import { CDP_STRINGS } from 'apps/customer-data-platform/src/Constants/Strings.constants';
import { TFunction } from 'i18next';
import property from 'lodash/property';
import { CUSTOMER_PROFILE_EMPTY_VALUE } from '../../CustomerProfile.constants';

export const EVENT_OCCURRENCE_DAY = `days`;
export const EVENT_OCCURRENCE_HOUR = `hours`;
export const EVENT_OCCURRENCE_MINUTE = `minutes`;

export type EventOccurrenceUnit =
  | typeof EVENT_OCCURRENCE_DAY
  | typeof EVENT_OCCURRENCE_HOUR
  | typeof EVENT_OCCURRENCE_MINUTE;

/**
 * An event occurrence data transfer object.
 */
export interface EventOccurrenceDto {
  /**
   * The event occurrence count.
   * @memberof EventOccurrenceDto
   */
  value: number;

  /**
   * The relevance value of the event occurrence (e.g. "7" for "7 days").
   * @memberof EventOccurrenceDto
   */
  relevance: number;

  /**
   * The event occurrence unit type.
   * @memberof EventOccurrenceDto
   */
  unit: EventOccurrenceUnit;
}

/**
 * Formats the number of occurrences into a human readable value.
 * @param {EventOccurrenceDto} occurrence The event occurrence dto.
 */
export const formatEventOccurrence = (
  occurrence: EventOccurrenceDto,
  t: TFunction<'translation', undefined>,
): string => {
  const { relevance, value, unit } = occurrence;
  let unitKey = CDP_STRINGS.eventOccurrenceRelevanceDays_one;
  let valueKey = `eventOccurrenceCount_one`;
  // eslint-disable-next-line default-case
  switch (unit) {
    case EVENT_OCCURRENCE_DAY:
      unitKey = `eventOccurrenceRelevanceDays_one`;
      break;
    case EVENT_OCCURRENCE_HOUR:
      unitKey = `eventOccurrenceRelevanceHours_one`;
      break;
    case EVENT_OCCURRENCE_MINUTE:
      unitKey = `eventOccurrenceRelevanceMinutes_one`;
      break;
  }
  if (!isNaN(relevance) && (relevance > 1 || relevance === 0)) {
    unitKey = `${unitKey.replace(`_one`, `_other`)}`;
    valueKey = `eventOccurrenceCountIn_one`;
  }
  if (value > 1 || value === 0) {
    if (!isNaN(relevance)) {
      valueKey = `eventOccurrenceCountIn_other`;
    } else {
      valueKey = `eventOccurrenceCount_other`;
    }
  }
  if (!isNaN(relevance)) {
    return `${t(property(valueKey)(CDP_STRINGS) as string, { count: value })} ${t(
      property(unitKey)(CDP_STRINGS) as string,
      {
        count: relevance,
        unit,
      },
    )}`;
  } else {
    return `${t(property(valueKey)(CDP_STRINGS) as string, { count: value })}`;
  }
};

export type GetAttributeValueType = boolean | string | number | EventOccurrenceDto;

/**
 * Get display value for an SCV attribute.
 * @param {GetAttributeValueType} value The value for the attribute.
 * @param {string} type The type of attribute.
 * @param {TFunction<"translation", undefined>} t i18next translation function.
 * @export
 * @returns {string} The string to display.
 */
export const getAttributeValue = (
  value: GetAttributeValueType,
  type: string,
  t: TFunction<'translation', undefined>,
): string => {
  switch (type) {
    case 'boolean':
      return value ? t(`Yes`) : t(`No`);
    case 'date':
      return formatDate(value as string);
    case 'event':
      // Note: to call with event type we expect a Dto.
      return formatEventOccurrence(value as EventOccurrenceDto, t);
    case 'number':
      return String(value);
    case 'object':
      if (value && Object.keys(value).length > 0) {
        return Object.values(value).join(`, `);
      }
      return CUSTOMER_PROFILE_EMPTY_VALUE;
    case 'string':
      return t(value as string);
    default:
      return CUSTOMER_PROFILE_EMPTY_VALUE;
  }
};
